import React, { useCallback } from 'react';
import { Button } from 'react-bootstrap';
import './styles.scss';
import { Typography } from 'Components/atoms';
import { StepCircle } from 'Components/atoms/StepCircle';
import { ConditionRow } from 'Components/molecules/ConditionRow';
import { ActionRow } from 'Components/molecules/ActionRow';
import { AdvGroupBox } from 'Components/organisms/AdvGroupBox';
import { ActionEntry, CondEntry, GroupNode } from 'Core-utils/types/rules.types';
import { emptyCond, emptyGroup } from 'Core-utils/helpers/ruleBuilderHelpers';
import { useI18n } from 'Hooks/useI18n';
import {
    RULE_BUILDER_ADV_LOGIC_TITLE,
    RULE_BUILDER_ADV_LOGIC_SUBTITLE,
    RULE_BUILDER_ADV_COLLAPSE_ALL,
    RULE_BUILDER_ADV_ADD_GROUP,
    RULE_BUILDER_ADV_ADD_CONDITION,
    RULE_BUILDER_THEN_TITLE,
    RULE_BUILDER_THEN_SUBTITLE,
    RULE_BUILDER_ELSE_TITLE,
    RULE_BUILDER_ELSE_SUBTITLE,
    RULE_BUILDER_OPTIONAL_LABEL,
} from 'Core-utils/constants/constants';

export interface AdvancedModeProps {
    rootChildren: Array<CondEntry | GroupNode>;
    thenActions: ActionEntry[];
    elseActions: ActionEntry[];
    onRootChange: (updated: Array<CondEntry | GroupNode>) => void;
    onThenChange: (id: string, patch: Partial<ActionEntry>) => void;
    onThenRemove: (id: string) => void;
    onThenAdd: () => void;
    onElseChange: (id: string, patch: Partial<ActionEntry>) => void;
    onElseRemove: (id: string) => void;
    onElseAdd: () => void;
}

const AdvancedMode = ({
    rootChildren, thenActions, elseActions,
    onRootChange, onThenChange, onThenRemove, onThenAdd: _onThenAdd,
    onElseChange, onElseRemove, onElseAdd: _onElseAdd,
}: AdvancedModeProps) => {
    const { t } = useI18n();

    const addGroup = useCallback(() => onRootChange([...rootChildren, emptyGroup()]), [rootChildren, onRootChange]);
    const addRootCond = useCallback(() => onRootChange([...rootChildren, emptyCond()]), [rootChildren, onRootChange]);

    const updateChild = useCallback(
        (id: string, updated: CondEntry | GroupNode) =>
            onRootChange(rootChildren.map((c) => (c.id === id ? updated : c))),
        [rootChildren, onRootChange],
    );

    const removeChild = useCallback(
        (id: string) => onRootChange(rootChildren.filter((c) => c.id !== id)),
        [rootChildren, onRootChange],
    );

    return (
        <div className="d-flex flex-column gap-3">
            {/* Rule logic header */}
            <div className="rb-adv__header">
                <div>
                    <Typography variant="body2" className="rb-adv__section-title mb-0">
                        {t(RULE_BUILDER_ADV_LOGIC_TITLE, 'Rule logic')}
                    </Typography>
                    <Typography variant="caption1" color="text-low-emphasis" style={{ fontSize: 11 }}>
                        {t(RULE_BUILDER_ADV_LOGIC_SUBTITLE, 'Build the logic that determines when this rule runs.')}
                    </Typography>
                </div>
                <div className="d-flex align-items-center gap-2 ms-auto">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="rb-adv__header-btn"
                        onClick={() => {/* collapse all – visual only */}}
                    >
                        {t(RULE_BUILDER_ADV_COLLAPSE_ALL, 'Collapse all')}
                    </Button>
                    <Button size="sm" className="rb-adv__add-group-btn" onClick={addGroup}>
                        {t(RULE_BUILDER_ADV_ADD_GROUP, '+ Add group')}
                    </Button>
                </div>
            </div>

            {/* Root-level items */}
            <div className="d-flex flex-column gap-3">
                {rootChildren.map((child) =>
                    child.kind === 'cond' ? (
                        <ConditionRow
                            key={child.id}
                            cond={child}
                            showDragHandle
                            onChange={(patch) => updateChild(child.id, { ...child, ...patch })}
                            onRemove={() => removeChild(child.id)}
                        />
                    ) : (
                        <AdvGroupBox
                            key={child.id}
                            node={child}
                            depth={0}
                            onChange={(updated) => updateChild(child.id, updated)}
                            onDelete={() => removeChild(child.id)}
                        />
                    ),
                )}
            </div>

            {/* Add group / condition */}
            <div className="d-flex gap-2">
                <button type="button" className="rb-add-link-outline" onClick={addGroup}>
                    {t(RULE_BUILDER_ADV_ADD_GROUP, '+ Add group')}
                </button>
                <button type="button" className="rb-add-link-outline" onClick={addRootCond}>
                    {t(RULE_BUILDER_ADV_ADD_CONDITION, '+ Add condition')}
                </button>
            </div>

            {/* Then */}
            <div className="rb-step-card">
                <div className="rb-step-card__header">
                    <StepCircle n={2} />
                    <div>
                        <Typography variant="body2" className="rb-step-card__title mb-0">
                            {t(RULE_BUILDER_THEN_TITLE, 'Then do this')}
                        </Typography>
                        <Typography variant="caption1" color="text-low-emphasis" className="d-block mb-0" style={{ fontSize: 11 }}>
                            {t(RULE_BUILDER_THEN_SUBTITLE, 'Choose what should happen when the conditions are met.')}
                        </Typography>
                    </div>
                </div>
                <div className="rb-step-card__body">
                    <div className="d-flex flex-column gap-2">
                        {thenActions.map((a) => (
                            <ActionRow
                                key={a.id}
                                entry={a}
                                label="Then"
                                onChange={(p) => onThenChange(a.id, p)}
                                onRemove={() => onThenRemove(a.id)}
                                canRemove={thenActions.length > 1}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* Otherwise */}
            <div className="rb-step-card rb-step-card--optional">
                <div className="rb-step-card__header">
                    <StepCircle n={3} />
                    <div>
                        <Typography variant="body2" className="rb-step-card__title mb-0">
                            {t(RULE_BUILDER_ELSE_TITLE, 'Otherwise do this')} <span className="rb-step-card__optional-label">({t(RULE_BUILDER_OPTIONAL_LABEL, 'optional')})</span>
                        </Typography>
                        <Typography variant="caption1" color="text-low-emphasis" className="d-block mb-0" style={{ fontSize: 11 }}>
                            {t(RULE_BUILDER_ELSE_SUBTITLE, 'Choose what should happen when the conditions are not met.')}
                        </Typography>
                    </div>
                </div>
                <div className="rb-step-card__body">
                    <div className="d-flex flex-column gap-2">
                        {elseActions.map((a) => (
                            <ActionRow
                                key={a.id}
                                entry={a}
                                label="Else"
                                onChange={(p) => onElseChange(a.id, p)}
                                onRemove={() => onElseRemove(a.id)}
                                canRemove={elseActions.length > 1}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdvancedMode;
